import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Upload, Trash2, Users } from "lucide-react";

function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length);
  if (lines.length === 0) return { headers: [], rows: [] };
  const split = (l) => l.split(",").map((c) => c.replace(/^"|"$/g, "").trim());
  const headers = split(lines[0]).map((h) => h.toLowerCase());
  const rows = lines.slice(1).map((l) => {
    const parts = split(l);
    const obj = {};
    headers.forEach((h, i) => (obj[h] = parts[i] || ""));
    return obj;
  });
  return { headers, rows };
}

export default function Customers() {
  const [items, setItems] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [openImport, setOpenImport] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [csvRows, setCsvRows] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);

  const load = () => api.get("/customers").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const addCustomer = async () => {
    if (!form.name || !form.email) { toast.error("Name and email required"); return; }
    try {
      await api.post("/customers", form);
      toast.success("Customer added");
      setForm({ name: "", email: "", phone: "" });
      setOpenAdd(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add customer");
    }
  };

  const onCsv = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const rd = new FileReader();
    rd.onload = () => {
      const { headers, rows } = parseCsv(String(rd.result));
      setCsvHeaders(headers);
      setCsvRows(rows);
    };
    rd.readAsText(f);
  };

  const preview = useMemo(() => csvRows.slice(0, 5).map((r) => ({
    name: r.name || r["full name"] || "",
    email: r.email || r["email address"] || "",
    phone: r.phone || r["phone number"] || "",
  })), [csvRows]);

  const doImport = async () => {
    const rows = csvRows.map((r) => ({
      name: r.name || r["full name"] || "",
      email: r.email || r["email address"] || "",
      phone: r.phone || r["phone number"] || "",
    })).filter((r) => r.email);
    if (rows.length === 0) { toast.error("No valid rows"); return; }
    try {
      const { data } = await api.post("/customers/import", { rows });
      toast.success(data.summary || `${data.added} imported · ${data.skipped.length} skipped`);
      setOpenImport(false);
      setCsvRows([]); setCsvHeaders([]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const statusOf = (c) => {
    const lr = c.last_request;
    if (!lr) return { label: "—", tone: "slate" };
    if (lr.clicked_at) return { label: "Clicked", tone: "primary" };
    if (lr.opened_at) return { label: "Opened", tone: "blue" };
    if (lr.status === "failed") return { label: "Failed", tone: "red" };
    return { label: "Sent", tone: "slate" };
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-slate-900">Customers</h1>
          <p className="text-slate-500 mt-1">{items.length} total</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={openImport} onOpenChange={setOpenImport}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-full h-11" data-testid="customers-import-btn"><Upload className="h-4 w-4 mr-2" /> Import CSV</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Import from CSV</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-slate-500">Columns: <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">name, email, phone</code>. Duplicates by email are skipped.</p>
                <Input type="file" accept=".csv,text/csv" onChange={onCsv} className="h-12" data-testid="customers-csv-input" />
                {preview.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="text-xs px-3 py-2 bg-slate-50 border-b">Preview (first 5 of {csvRows.length})</div>
                    <Table>
                      <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {preview.map((r, i) => (
                          <TableRow key={i}><TableCell>{r.name}</TableCell><TableCell>{r.email}</TableCell><TableCell>{r.phone}</TableCell></TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpenImport(false)}>Cancel</Button>
                <Button onClick={doImport} disabled={csvRows.length === 0} className="bg-primary hover:bg-primary/90" data-testid="customers-import-submit">Import {csvRows.length ? `${csvRows.length} rows` : ""}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={openAdd} onOpenChange={setOpenAdd}>
            <DialogTrigger asChild>
              <Button className="rounded-full h-11 bg-primary hover:bg-primary/90" data-testid="customers-add-btn"><Plus className="h-4 w-4 mr-2" /> Add customer</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add customer</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12" data-testid="customer-form-name" />
                <Input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12" data-testid="customer-form-email" />
                <Input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-12" data-testid="customer-form-phone" />
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpenAdd(false)}>Cancel</Button>
                <Button onClick={addCustomer} className="bg-primary hover:bg-primary/90" data-testid="customer-form-submit">Add</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="rounded-2xl border-slate-200 overflow-hidden">
        {items.length === 0 ? (
          <div className="p-16 text-center" data-testid="customers-empty">
            <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-slate-100 grid place-items-center"><Users className="h-6 w-6 text-slate-400" /></div>
            <p className="font-medium text-slate-800">Add your first customer</p>
            <p className="text-sm text-slate-500 mb-4">Or import many via CSV upload.</p>
            <div className="flex justify-center gap-2">
              <Button onClick={() => setOpenAdd(true)} className="rounded-full bg-primary hover:bg-primary/90">Add customer</Button>
              <Button variant="outline" onClick={() => setOpenImport(true)} className="rounded-full">Import CSV</Button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="hidden md:table-cell">Phone</TableHead>
                <TableHead className="hidden md:table-cell">Last request</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c, i) => {
                const s = statusOf(c);
                return (
                  <TableRow key={c.id} data-testid={`customer-row-${c.id}`} className="fade-up" style={{ animationDelay: `${i * 20}ms` }}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="text-slate-600">{c.email}</TableCell>
                    <TableCell className="hidden md:table-cell text-slate-600">{c.phone || "—"}</TableCell>
                    <TableCell className="hidden md:table-cell text-slate-500">{c.last_request ? new Date(c.last_request.created_at).toLocaleDateString() : "—"}</TableCell>
                    <TableCell><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.tone === "primary" ? "bg-primary/10 text-primary" : s.tone === "blue" ? "bg-blue-50 text-blue-700" : s.tone === "red" ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-600"}`}>{s.label}</span></TableCell>
                    <TableCell>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-red-600" data-testid={`customer-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete {c.name}?</AlertDialogTitle>
                            <AlertDialogDescription>This can't be undone. Their request history will also be removed from the list.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => del(c.id)} className="bg-red-600 hover:bg-red-700" data-testid={`customer-delete-confirm-${c.id}`}>Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
